import { memo, useState } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow';
import { LayoutGrid, Edit2, Trash2, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, Button, Label } from '@caruso/ui';
import { useQuery } from '@tanstack/react-query';
import { boardsService } from '@/services/boards.service';
import { useFlowContext } from '../FlowContext';

interface BoardStepNodeData {
  boardId?: string;
  stepId?: string;
  boardName?: string;
  stepName?: string;
}

export const BoardStepNode = memo(({ data, id }: NodeProps<BoardStepNodeData>) => {
  const { deleteElements, setNodes } = useReactFlow();
  const [isEditing, setIsEditing] = useState(false);
  const [selectedStepId, setSelectedStepId] = useState<string>(data.stepId || '');

  // Obtém o boardId do contexto do fluxo (prioridade) ou do data do nó
  const flowContext = useFlowContext();
  const flowBoardId = flowContext.boardId || data.boardId;
  const flowBoardName = flowContext.boardName || data.boardName;

  // Busca os dados do quadro do fluxo
  const { data: board } = useQuery({
    queryKey: ['board', flowBoardId],
    queryFn: () => boardsService.getById(flowBoardId!),
    enabled: isEditing && !!flowBoardId,
  });

  const handleDelete = () => {
    deleteElements({ nodes: [{ id }] });
  };

  const handleSave = () => {
    const step = board?.steps?.find((s) => s.id === selectedStepId);

    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === id) {
          return {
            ...node,
            data: {
              ...node.data,
              stepId: selectedStepId,
              stepName: step?.name,
            },
          };
        }
        return node;
      })
    );
    setIsEditing(false);
  };

  const isConfigured = data.stepId;
  const hasBoardConfigured = !!flowBoardId;

  return (
    <>
      <div className="bg-indigo-500/10 border-2 border-indigo-500 rounded-lg p-4 min-w-[280px] shadow-lg">
        <Handle type="target" position={Position.Top} className="!bg-indigo-500" />

        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-indigo-500 flex items-center justify-center">
              <LayoutGrid className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-bold text-indigo-600">Mover no Quadro</div>
              <div className="text-xs text-gray-500">Altera posicao no Kanban</div>
            </div>
          </div>

          <div className="flex gap-1">
            <button
              onClick={() => setIsEditing(true)}
              className="p-1.5 hover:bg-indigo-100 rounded transition-colors"
              title="Editar"
            >
              <Edit2 className="w-4 h-4 text-indigo-600" />
            </button>
            <button
              onClick={handleDelete}
              className="p-1.5 hover:bg-red-100 rounded transition-colors"
              title="Excluir"
            >
              <Trash2 className="w-4 h-4 text-red-600" />
            </button>
          </div>
        </div>

        <div className="space-y-2 text-sm">
          {!hasBoardConfigured ? (
            <div className="text-xs text-yellow-600 italic flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              Configure um quadro no fluxo primeiro
            </div>
          ) : isConfigured ? (
            <>
              <div className="text-xs text-gray-600">
                <span className="font-medium">Quadro:</span> {flowBoardName}
              </div>
              <div className="text-xs text-gray-600">
                <span className="font-medium">Mover para:</span> {data.stepName}
              </div>
            </>
          ) : (
            <div className="text-xs text-indigo-600 italic">
              Clique para configurar
            </div>
          )}
        </div>

        <Handle type="source" position={Position.Bottom} className="!bg-indigo-500" />
      </div>

      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="bg-brand-ink border-brand-gold/20 text-brand-ivory">
          <DialogHeader>
            <DialogTitle className="text-brand-gold flex items-center gap-2">
              <LayoutGrid className="w-5 h-5" />
              Configurar Movimento no Quadro
            </DialogTitle>
            <DialogDescription className="text-brand-ivory/60">
              Selecione o passo para onde o cliente sera movido
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            {!hasBoardConfigured ? (
              <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded">
                <p className="text-sm text-yellow-400 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Este fluxo nao possui um quadro configurado.
                </p>
                <p className="text-xs text-yellow-400/70 mt-2">
                  Para usar este no, primeiro configure um quadro nas configuracoes do fluxo (icone de engrenagem no cabecalho).
                </p>
              </div>
            ) : (
              <>
                <div className="p-3 bg-indigo-500/10 border border-indigo-500/20 rounded">
                  <p className="text-sm text-brand-ivory">
                    <span className="text-brand-ivory/60">Quadro:</span>{' '}
                    <span className="font-medium">{flowBoardName || board?.name || 'Carregando...'}</span>
                  </p>
                </div>

                {board?.steps && board.steps.length > 0 ? (
                  <div>
                    <Label className="text-brand-ivory/80">
                      Mover para o Passo <span className="text-red-400">*</span>
                    </Label>
                    <select
                      value={selectedStepId}
                      onChange={(e) => setSelectedStepId(e.target.value)}
                      className="w-full bg-brand-ink/50 border border-brand-gold/20 text-brand-ivory mt-1 rounded px-3 py-2 h-10"
                    >
                      <option value="">Selecione um passo</option>
                      {board.steps.map((step) => (
                        <option key={step.id} value={step.id}>
                          {step.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-brand-ivory/50 mt-1">
                      O cliente sera movido para este passo quando o fluxo atingir este no
                    </p>
                  </div>
                ) : (
                  <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded">
                    <p className="text-sm text-yellow-400">
                      Este quadro nao possui passos configurados. Adicione passos ao quadro primeiro.
                    </p>
                  </div>
                )}

                <div className="p-4 bg-brand-gold/5 rounded border border-brand-gold/20">
                  <p className="text-sm text-brand-ivory/80 mb-2 font-medium">
                    Como funciona:
                  </p>
                  <ul className="text-xs text-brand-ivory/60 space-y-1 list-disc list-inside">
                    <li>Quando o fluxo atingir este no, o cliente sera movido para o passo selecionado</li>
                    <li>Voce pode visualizar o progresso dos clientes no quadro Kanban</li>
                    <li>Se o passo tiver um fluxo de gatilho configurado, ele sera disparado automaticamente</li>
                  </ul>
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <Button
              variant="outline"
              onClick={() => setIsEditing(false)}
              className="border-brand-gold/20 text-brand-ivory hover:bg-brand-gold/10"
            >
              Cancelar
            </Button>
            <Button
              variant="premium"
              onClick={handleSave}
              disabled={!hasBoardConfigured || !selectedStepId}
              className="bg-indigo-500 hover:bg-indigo-600"
            >
              Salvar Configuracao
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
});

BoardStepNode.displayName = 'BoardStepNode';
